import { memo } from 'react'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'

import PriorityIcon from '@icons/Priority'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import { TaskStatus } from '@shared/utils'

import Avatar from '@components/Avatar'
import EntityFlag, { useEntityFlagState } from '@components/EntityFlag'
import { useCommonFilters, useTaskLabels } from '@components/Kanban/Kanban.hooks'
import { getTaskOverdueStatus } from '@components/Kanban/Kanban.utils'
import Menu from '@components/Menu'

import { DragSource } from '../Dnd'
import styles from './Task.styles'

// Pure presentational card. Memoized so it stays idle while the surrounding
// DragSource / DragOverlay re-render 60Hz during a drag.
const TaskCard = memo(function TaskCard({ label, item, isFlagged, overdueStatus, daysPassed, overlay, onClick }) {
  return (
    <Paper onClick={onClick} sx={[styles.content({ isFlagged, overlay }), styles[`${overdueStatus}Card`]]}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography noWrap variant="h4">
          {label}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <EntityFlag value={isFlagged} />
          {!overlay && <MoreMenu id={item.id} />}
        </Stack>
      </Stack>
      {item.patient ? (
        <>
          <Stack>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                  MRN
                </Typography>
                <Typography>{item.patient.id}</Typography>
              </Stack>
              <Typography variant="body2">{dayjs(item.createdAt).format('L')}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                  STATE
                </Typography>
                {item.patient.state ? (
                  <Typography>{item.patient.state}</Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Not Set
                  </Typography>
                )}
              </Stack>
              <Typography variant="body2">{item.patient.providerTypes?.map((p) => p.name).join(', ')}</Typography>
            </Stack>
            <Typography noWrap variant="body2">
              {item.patient.fullName}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <PriorityIcon value={item.priority} />
            </Box>
            {item.patient.provider && (
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Typography noWrap variant="subtitle2">
                  {item.patient.provider.fullName}
                </Typography>
                <Avatar size="xs" user={item.patient.provider} hover="card" />
              </Stack>
            )}
          </Stack>
        </>
      ) : (
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <PriorityIcon value={item.priority} />
          <Typography variant="body2">{dayjs(item.createdAt).format('L')}</Typography>
        </Stack>
      )}
      <Divider />
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {item.assignee && <Avatar size="xs" user={item.assignee} hover="card" />}
        <Typography variant="body2" sx={{ flex: '1 1 auto' }}>
          {item.identifier}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: overdueStatus ? `${overdueStatus}.main` : 'text.primary',
            fontWeight: overdueStatus ? 'bold' : 'normal',
          }}
        >
          Day: {daysPassed}
        </Typography>
      </Stack>
    </Paper>
  )
})

function Task({ label, item }) {
  const [, updateFilters] = useCommonFilters()
  const disabled = [TaskStatus.Completed, TaskStatus.Expired].includes(item.status)
  const [overdueStatus, daysPassed] = getTaskOverdueStatus(item)
  const [isFlagged] = useEntityFlagState('tasks', item.id)

  return (
    <DragSource id={item.id} item={item} fromZoneId={item.status} disabled={disabled}>
      <TaskCard
        label={label}
        item={item}
        isFlagged={isFlagged}
        overdueStatus={overdueStatus}
        daysPassed={daysPassed}
        onClick={() => updateFilters({ task: item.identifier })}
      />
    </DragSource>
  )
}

Task.propTypes = {
  index: PropTypes.number,
  item: PropTypes.object,
}

function TaskOverlayInner({ item }) {
  const taskTypeToLabel = useTaskLabels()
  const [isFlagged] = useEntityFlagState('tasks', item.id)
  const [overdueStatus, daysPassed] = getTaskOverdueStatus(item)
  const label = taskTypeToLabel[item.kind] || item.kind

  return <TaskCard label={label} item={item} isFlagged={isFlagged} overdueStatus={overdueStatus} daysPassed={daysPassed} overlay />
}

const TaskOverlay = memo(TaskOverlayInner)

function Loading() {
  return (
    <Paper sx={styles.content()}>
      <Typography noWrap variant="h4">
        <Skeleton width={200} />
      </Typography>
      <Stack>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
            <Typography variant="subtitle2">
              <Skeleton width={30} />
            </Typography>
            <Typography>
              <Skeleton width={50} />
            </Typography>
          </Stack>
          <Typography variant="body2">
            <Skeleton width={70} />
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
            <Typography variant="subtitle2">
              <Skeleton width={40} />
            </Typography>
            <Typography>
              <Skeleton width={25} />
            </Typography>
          </Stack>
          <Typography variant="body2">
            <Skeleton width={80} />
          </Typography>
        </Stack>
        <Typography variant="body2">
          <Skeleton width={150} />
        </Typography>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Skeleton width={24} height={24} />
        </Box>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Typography variant="subtitle2">
            <Skeleton width={100} />
          </Typography>
          <Skeleton variant="rounded" width={24} height={24} />
        </Stack>
      </Stack>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Skeleton variant="rounded" width={24} height={24} />
        <Typography variant="body2">
          <Skeleton width={80} />
        </Typography>
      </Stack>
    </Paper>
  )
}

function MoreMenu({ id }) {
  const [flagged, updateFlags] = useEntityFlagState('tasks', id)

  return (
    <Menu>
      {({ close }) => (
        <MenuItem
          onClick={(event) => {
            event.stopPropagation()
            close()
            updateFlags((flags) => ({ ...flags, [id]: !flagged }))
          }}
        >
          {flagged ? 'Remove' : 'Add'} flag
        </MenuItem>
      )}
    </Menu>
  )
}

const MemoTask = memo(Task)
MemoTask.Loading = Loading
MemoTask.Overlay = TaskOverlay

export default MemoTask
